import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { CheckCircle, Clock, Eye } from "lucide-react";
import { useGetInProgressReport, InProgressAssignment, InProgressAssignmentStep } from "@/lib/api/knowledge";
import { CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface StepsModalProps {
  assignment: InProgressAssignment | null;
  isOpen: boolean;
  onClose: () => void;
}

function StepsModal({ assignment, isOpen, onClose }: StepsModalProps) {
  const navigate = useNavigate();

  if (!assignment) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Steps for {assignment.knowledge_item.title}</DialogTitle>
          <DialogDescription>
            Assigned to {assignment.assignee.name} • {assignment.steps_completed} of {assignment.total_steps} completed
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[500px] pr-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead>Step</TableHead>
                <TableHead className="w-[120px]">Completion By</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[150px]">Completed By</TableHead>
                <TableHead className="w-[150px]">Completed At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignment.all_steps.map((step: InProgressAssignmentStep) => (
                <TableRow 
                  key={step.step_id}
                  className={step.is_active ? "bg-blue-50" : ""}
                >
                  <TableCell className="font-medium">
                    {step.order}
                    {step.is_blocker && <span className="text-red-500 ml-1">*</span>}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{step.title}</div>
                      {step.description && (
                        <div className="text-sm text-muted-foreground">{step.description}</div>
                      )}
                      {step.is_active && (
                        <Badge variant="outline" className="mt-1 bg-blue-100 text-blue-700 border-blue-200">
                          Active Step
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm capitalize">{step.completion_by}</TableCell>
                  <TableCell>
                    {step.is_completed ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Complete
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {step.completed_by?.name || "-"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {step.completed_at 
                      ? moment.tz(step.completed_at, CommonDestinationTimezone).format("MM/DD/YY hh:mm A")
                      : "-"
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => {
            navigate(`/tasks/${assignment.knowledge_item.id}`);
            onClose();
          }}>
            View Full Task
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InProgressReport() {
  const navigate = useNavigate();
  const inProgressRes = useGetInProgressReport();
  const [selectedAssignment, setSelectedAssignment] = useState<InProgressAssignment | null>(null);
  const [isStepsModalOpen, setIsStepsModalOpen] = useState(false);

  const loadingOrErrorUi = GenerateLoadingOrError([inProgressRes]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const assignments = inProgressRes.data?.data || [];

  const handleViewSteps = (assignment: InProgressAssignment) => {
    setSelectedAssignment(assignment);
    setIsStepsModalOpen(true);
  };

  const columns: DataTableColumn<InProgressAssignment>[] = [
    {
      id: "knowledge_item_title",
      accessorKey: "knowledge_item.title",
      header: "Task",
      cell: (info) => {
        const assignment = info.row.original;
        return (
          <div>
            <div 
              className="font-medium cursor-pointer hover:underline"
              onClick={() => navigate(`/tasks/${assignment.knowledge_item.id}`)}
            >
              {info.getValue() as string}
            </div>
            {assignment.knowledge_item.description && (
              <div className="text-sm text-muted-foreground truncate max-w-md">
                {assignment.knowledge_item.description}
              </div>
            )}
          </div>
        );
      },
      size: 250,
    },
    {
      id: "assignee_name",
      accessorKey: "assignee.name",
      header: "Assignee",
      size: 180,
    },
    {
      id: "trainer_name",
      accessorKey: "trainer.name",
      header: "Trainer",
      size: 180,
    },
    {
      id: "backup_trainer_name",
      accessorKey: "backup_trainer.name",
      header: "Backup Trainer",
      size: 180,
    },
    {
      id: "assigned_at",
      accessorKey: "assigned_at",
      header: "Assigned Date",
      cell: (info) => {
        return moment.tz(info.getValue() as Date, CommonDestinationTimezone).format("MM/DD/YY");
      },
      size: 120,
    },
    {
      id: "progress",
      header: "Progress",
      cell: (info) => {
        const assignment = info.row.original;
        const percentage = assignment.total_steps > 0 
          ? Math.round((assignment.steps_completed / assignment.total_steps) * 100)
          : 0;
        
        return (
          <div className="flex flex-col gap-1">
            <div className="text-sm font-medium">
              {assignment.steps_completed} / {assignment.total_steps}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <div className="text-xs text-muted-foreground">{percentage}%</div>
          </div>
        );
      },
      size: 150,
      enableSorting: false,
    },
    {
      id: "active_step",
      header: "Current Step",
      cell: (info) => {
        const assignment = info.row.original;
        if (!assignment.active_step) {
          return <span className="text-muted-foreground">All steps completed</span>;
        }
        return (
          <div>
            <div className="text-sm">
              Step {assignment.active_step.order}: {assignment.active_step.title}
            </div>
          </div>
        );
      },
      size: 200,
      enableSorting: false,
    },
    {
      id: "actions",
      header: "Actions",
      cell: (info) => {
        const assignment = info.row.original;
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewSteps(assignment)}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            View Steps
          </Button>
        );
      },
      size: 120,
      disableRowClick: true,
      enableFiltering: false,
      enableSorting: false,
    },
  ];

  return (
    <div className="p-4 space-y-6">
      <DataTable
        tableId="InProgressAssignments"
        columns={columns}
        data={assignments}
        enableSorting
        enableMultiSort
        enableFiltering
        enableGrouping
        enableColumnVisibility
        enableColumnResizing
        enableColumnPinning
        stickyHeaders
        filterStyle="select"
        filterMode="internal"
        defaultSorting={[{ id: "assigned_at", desc: true }]}
        tableClassName="h-[calc(100vh-15rem)] overflow-y-auto"
        emptyMessage={inProgressRes.isFetching ? "Loading..." : "No in-progress assignments found"}
        persistColumnSettings
      />

      <StepsModal
        assignment={selectedAssignment}
        isOpen={isStepsModalOpen}
        onClose={() => {
          setIsStepsModalOpen(false);
          setSelectedAssignment(null);
        }}
      />
    </div>
  );
}
